// Code generated by contracts/generate.ts. DO NOT EDIT.
//
// Edit the JSON under contracts/data/ and run `pnpm contracts:generate`.
// CI fails if this file is hand-edited or left stale.

/**
 * The tracks a song can be broken into. `haptic-bass` and `haptic-drums` are
 * authored as separate segment data and never go through the audio renderer.
 */
export type TrackType =
  | 'vocals'
  | 'bass'
  | 'drums'
  | 'melody'
  | 'haptic-drums'
  | 'haptic-bass';

/** Every TrackType value, in declaration order. */
export const ALL_TRACK_TYPES: readonly TrackType[] = Object.freeze([
  'vocals',
  'bass',
  'drums',
  'melody',
  'haptic-drums',
  'haptic-bass',
]);

/**
 * Which pitch range of a track to play. Only vocals, bass and melody are
 * authored in more than one range; everything else is always `mid`.
 */
export type AudioRangeType =
  | 'low'
  | 'mid'
  | 'high';

/** Every AudioRangeType value, in declaration order. */
export const ALL_AUDIO_RANGE_TYPES: readonly AudioRangeType[] = Object.freeze([
  'low',
  'mid',
  'high',
]);

/**
 * Per-track settings. Every field is optional: an absent key means "leave
 * this control at whatever the reader's own default is", which is not the
 * same as a present key holding a default-looking value.
 */
export interface TrackAudioVisualSettings {
  /** 0-100 per track. Not decibels, and not a gain multiplier. */
  trackVolume0To100?: TrackTypeOptionalMap<number>;
  isTrackMuted?: TrackTypeOptionalMap<boolean>;
  isTrackSolo?: TrackTypeOptionalMap<boolean>;
  /**
   * Only meaningful for the range-supporting tracks; a value on any other
   * track is ignored.
   */
  trackRange?: TrackTypeOptionalMap<AudioRangeType>;
  /** -1 (hard left) to 1 (hard right). */
  trackPan?: TrackTypeOptionalMap<number>;
  /** CSS hex colour per track, as authored in the tool. */
  trackColors?: TrackTypeOptionalMap<string>;
}

/**
 * What a user's saved settings for one song actually contain: the track
 * settings plus the view settings the player keeps alongside them. Stored at
 * `users/{uid}/mySongs/{songId}.settings` and in the player's local
 * preferences.
 *
 * The track fields are spelled out rather than composed from
 * `TrackAudioVisualSettings` because the codegen supports no composition
 * keyword; they must stay identical to it. Absent means "the reader's own
 * default", which for the Kotlin side is the default written against each
 * field here.
 *
 * Documents written by player builds before this def existed hold
 * `trackPitch` (`LOW`/`MED`/`HIGH`) in place of `trackRange`, and old builds
 * can write that shape back at any time, so a reader has to accept both.
 */
export interface SavedSongSettings {
  /** 0-100 per track. Not decibels, and not a gain multiplier. */
  trackVolume0To100?: TrackTypeOptionalMap<number>;
  isTrackMuted?: TrackTypeOptionalMap<boolean>;
  isTrackSolo?: TrackTypeOptionalMap<boolean>;
  /**
   * Only meaningful for the range-supporting tracks; a value on any other
   * track is ignored.
   */
  trackRange?: TrackTypeOptionalMap<AudioRangeType>;
  /**
   * -1 (hard left) to 1 (hard right). Typed here so the website and desktop
   * can read it; the player neither reads nor writes it, so it stays out of
   * the Kotlin shape and is never emitted as an empty map.
   */
  trackPan?: TrackTypeOptionalMap<number>;
  /**
   * CSS hex colour per track. Same as `trackPan`: typed for the other
   * readers, not part of what the player stores.
   */
  trackColors?: TrackTypeOptionalMap<string>;
  /** Master volume 0-100, applied on top of the per-track volumes. */
  mainVolume?: number;
  /**
   * Whether the pre-mixed original recording is playing instead of the
   * stems.
   */
  isOriginalVersion?: boolean;
  /** Whether the lyrics overlay is shown. */
  showLyrics?: boolean;
  /** Horizontal timeline zoom, as a multiplier on pixels-per-millisecond. */
  zoomLevel?: number;
  /** Font size of the lyrics track, in scale-independent pixels. */
  lyricsFontSize?: number;
  /**
   * Track ids the user has left visible in the timeline. Empty means "show
   * every track", not "hide every track".
   */
  visibleTrackIds?: string[];
}

/**
 * The generation of rendered audio TRACK_DEFAULTS is tuned for. A song whose
 * metadata carries no audioRenderVersion predates loudness-matched stems and
 * needs LEGACY_TRACK_DEFAULTS instead.
 */
export const LEVEL_MATCHED_AUDIO_RENDER_VERSION = 2 as const;

/**
 * Defaults for songs whose stems are loudness-matched to each other. Flat
 * volumes, because the renderer now matches stems. The haptic values stay
 * where they are: haptic tracks are authored as separate segment data and
 * never go through the audio renderer, so their trims compensate for
 * something unrelated.
 */
export const TRACK_DEFAULTS: TrackAudioVisualSettings = {
  trackVolume0To100: {
    drums: 100,
    bass: 100,
    vocals: 100,
    melody: 100,
    'haptic-bass': 100,
    'haptic-drums': 73,
  },
  isTrackMuted: {
    drums: false,
    bass: false,
    vocals: false,
    melody: false,
    'haptic-bass': false,
    'haptic-drums': false,
  },
  isTrackSolo: {
    drums: false,
    bass: false,
    vocals: false,
    melody: false,
    'haptic-bass': false,
    'haptic-drums': false,
  },
  trackColors: {
    drums: '#b9e2eb',
    bass: '#e88eb3',
    vocals: '#fff',
    melody: '#e8e1a5',
  },
  trackRange: {
    bass: 'mid',
    vocals: 'mid',
    melody: 'mid',
  },
};

/**
 * Track volumes for songs rendered before stems were loudness-matched.
 * Hand-tuned trims compensating for a renderer that left every stem at
 * whatever level its soundfont preset happened to produce - melody came out
 * roughly 7-11 dB hotter than bass on the same song. Kept so songs a user
 * already downloaded keep sounding the way they were tuned to sound. Only
 * the volumes differ from TRACK_DEFAULTS; everything else is taken from
 * there.
 */
export const LEGACY_TRACK_DEFAULTS: TrackAudioVisualSettings = {
  trackVolume0To100: {
    drums: 90,
    bass: 75,
    vocals: 100,
    melody: 85,
    'haptic-bass': 100,
    'haptic-drums': 73,
  },
};

/**
 * Every field of a settings blob is optional and keyed by track type, because
 * a song need not have every track and a user need not have touched every
 * control. Declared here rather than generated: it is a mapped type, which
 * JSON Schema cannot express.
 */
export type TrackTypeOptionalMap<T> = { [id in TrackType]?: T };
